// ABOUTME: REST hook for configuration-template mutations (create, configure, apply).
// ABOUTME: Calls the /api/cpq/templates endpoints; list/edit/delete go through the custom page (GraphQL).

import { useCallback, useState } from 'react';

export type TemplateVisibility = 'private' | 'distributorship' | 'market';

interface ApiResponse<T> {
    success: boolean;
    data?: T;
    error?: string;
    message?: string;
}

async function apiFetch<T>(url: string, options?: RequestInit): Promise<T> {
    const response = await fetch(url, {
        headers: { 'Content-Type': 'application/json' },
        ...options,
    });
    const json: ApiResponse<T> = await response.json();
    if (!json.success) {
        throw new Error(json.error ?? 'Unknown error');
    }
    return json.data as T;
}

/**
 * Hook for configuration-template operations via the REST API.
 */
export function useTemplates() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const createTemplate = useCallback(
        async (payload: {
            title: string;
            description?: string;
            visibility: TemplateVisibility;
            configurationJson: string;
            prototypeId?: string;
        }) => {
            setLoading(true);
            setError(null);
            try {
                const result = await apiFetch<{ templateId: string }>('/api/cpq/templates', {
                    method: 'POST',
                    body: JSON.stringify(payload),
                });
                return result;
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to create template';
                setError(message);
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    const configureTemplate = useCallback(
        async (
            templateId: string,
            payload: { configurationJson: string; prototypeId?: string },
        ) => {
            setLoading(true);
            setError(null);
            try {
                await apiFetch(`/api/cpq/templates/${templateId}/configure`, {
                    method: 'POST',
                    body: JSON.stringify(payload),
                });
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to configure template';
                setError(message);
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    const applyTemplate = useCallback(
        async (
            templateId: string,
            payload: { offerId: string; sectionId?: string; quantity?: number },
        ) => {
            setLoading(true);
            setError(null);
            try {
                const result = await apiFetch<{ lineItemId: string }>(
                    `/api/cpq/templates/${templateId}/apply`,
                    {
                        method: 'POST',
                        body: JSON.stringify(payload),
                    },
                );
                return result;
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to apply template';
                setError(message);
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    return {
        loading,
        error,
        createTemplate,
        configureTemplate,
        applyTemplate,
    };
}
